import { Link } from 'react-router-dom';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '~/lib/cn.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-55';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-secondary dark:text-[#071427] dark:hover:bg-secondary',
  secondary: 'border border-line-strong text-heading hover:border-primary hover:text-primary',
  ghost: 'text-muted hover:text-heading',
  danger: 'border border-red-600/40 text-red-700 hover:bg-red-600/10 dark:text-red-300',
};

const SIZES: Record<Size, string> = {
  // 44px tall at md: a comfortable target on touch screens.
  md: 'min-h-11 px-5 py-2.5 text-sm',
  sm: 'min-h-9 px-3.5 py-1.5 text-sm',
};

function classes(variant: Variant, size: Size, className?: string): string {
  return cn(BASE, VARIANTS[variant], SIZES[size], className);
}

interface CommonProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  ...props
}: CommonProps & ComponentPropsWithoutRef<'button'>) {
  return <button type={type} className={classes(variant, size, className)} {...props} />;
}

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className,
  to,
  ...props
}: CommonProps & Omit<ComponentPropsWithoutRef<typeof Link>, 'children'>) {
  return <Link to={to} className={classes(variant, size, className)} {...props} />;
}

export function ExternalButtonLink({
  variant = 'secondary',
  size = 'md',
  className,
  ...props
}: CommonProps & ComponentPropsWithoutRef<'a'>) {
  return (
    <a className={classes(variant, size, className)} rel="noopener noreferrer" target="_blank" {...props} />
  );
}
